'use client';
import React, { useState, useEffect, useRef } from 'react';
import { X, Heart, MapPin, Loader2, CheckCircle, Building2, Phone, Mail, User, QrCode, ZoomIn, Download, Upload, Info, Store } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { fetchDonationHubs, fetchHubForPantry, insertHubDonation, sendPantryEmail, fetchPantriesForHub } from '@/lib/adminDataService';
import type { DonationHub, Pantry } from '@/lib/pantryData';
import { toast } from 'sonner';

interface DonateModalProps {
  onClose: () => void;
  preselectedPantryId?: string;
}

type DonationType = 'in-kind' | 'cash' | 'both' | '';

export default function DonateModal({ onClose, preselectedPantryId }: DonateModalProps) {
  const [step, setStep] = useState<'select' | 'hub-profile' | 'form' | 'done'>('select');
  const [hubs, setHubs] = useState<DonationHub[]>([]);
  const [selectedHub, setSelectedHub] = useState<DonationHub | null>(null);
  const [preselectedHubName, setPreselectedHubName] = useState<string | null>(null);
  const [preselectedPantryName, setPreselectedPantryName] = useState<string | null>(null);
  const [loadingHubs, setLoadingHubs] = useState(true);
  const [qrLightboxOpen, setQrLightboxOpen] = useState(false);
  // Pantries under hub
  const [hubPantries, setHubPantries] = useState<Pantry[]>([]);
  const [loadingHubPantries, setLoadingHubPantries] = useState(false);

  // Form state
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorContact, setDonorContact] = useState('');
  const [donationType, setDonationType] = useState<DonationType>('');
  const [inKindItems, setInKindItems] = useState('');
  const [cashAmount, setCashAmount] = useState('');
  const [cashMethod, setCashMethod] = useState('');
  const [message, setMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'gcash' | 'bank' | 'other' | ''>('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const screenshotRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function load() {
      setLoadingHubs(true);
      const hubsData = await fetchDonationHubs();
      setHubs(hubsData);

      // If preselected pantry, find its hub and skip hub selection
      if (preselectedPantryId) {
        const supabase = createClient();
        const { data: pantryData } = await supabase.from('pantries').select('pangalan').eq('id', preselectedPantryId).single();
        if (pantryData) setPreselectedPantryName((pantryData as Record<string, unknown>).pangalan as string);

        const hub = await fetchHubForPantry(preselectedPantryId);
        if (hub) {
          setSelectedHub(hub);
          setPreselectedHubName(hub.name);
          setStep('hub-profile');
          loadHubPantries(hub.id, hubsData);
        }
      }
      setLoadingHubs(false);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedPantryId]);

  const loadHubPantries = async (hubId: string, allHubs?: DonationHub[]) => {
    setLoadingHubPantries(true);
    try {
      const supabase = createClient();
      const pantryIds = await fetchPantriesForHub(hubId);
      if (pantryIds.length > 0) {
        const { data } = await supabase.from('pantries').select('id, pangalan, barangay, lungsod, pantry_status').in('id', pantryIds);
        setHubPantries((data ?? []) as Pantry[]);
      } else {
        setHubPantries([]);
      }
    } catch {
      setHubPantries([]);
    }
    setLoadingHubPantries(false);
  };

  const handleSelectHub = (hub: DonationHub) => {
    setSelectedHub(hub);
    setStep('hub-profile');
    loadHubPantries(hub.id);
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Images only.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB.'); return; }
    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshotPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!donorName.trim()) errs.name = 'Please enter your name.';
    if (!donorContact.trim()) errs.contact = 'Please enter your contact number or email.';
    if (!donationType) errs.donationType = 'Piliin ang uri ng donasyon.';
    if ((donationType === 'in-kind' || donationType === 'both') && !inKindItems.trim()) errs.inKindItems = 'Ilarawan ang iyong in-kind donation.';
    if ((donationType === 'cash' || donationType === 'both') && !cashAmount.trim()) errs.cashAmount = 'Ilagay ang halaga o paraan ng cash donation.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const getItemsString = () => {
    const parts: string[] = [];
    if ((donationType === 'in-kind' || donationType === 'both') && inKindItems.trim()) {
      parts.push(`In-kind: ${inKindItems.trim()}`);
    }
    if ((donationType === 'cash' || donationType === 'both') && cashAmount.trim()) {
      parts.push(`Cash/Fund Transfer: ${cashAmount.trim()}${cashMethod ? ` via ${cashMethod}` : ''}`);
    }
    return parts.join(' | ');
  };

  const uploadScreenshot = async (file: File): Promise<string | null> => {
    const supabase = createClient();
    const ext = file.name.split('.').pop() ?? 'png';
    const fileName = `donation-proof-${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage.from('donation-proofs').upload(fileName, file, { upsert: false });
    if (error) { console.error('Screenshot upload error:', error.message); return null; }
    const { data: urlData } = supabase.storage.from('donation-proofs').getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleSubmit = async () => {
    if (!validate() || !selectedHub) return;
    setIsSubmitting(true);

    let screenshotUrl: string | undefined;
    if (screenshotFile) {
      const url = await uploadScreenshot(screenshotFile);
      if (url) screenshotUrl = url;
    }

    const itemsString = getItemsString();

    const donation = await insertHubDonation({
      hubId: selectedHub.id,
      donorName: donorName.trim(),
      donorEmail: donorEmail.trim() || undefined,
      donorContact: donorContact.trim(),
      items: itemsString,
      message: message.trim() || undefined,
      paymentMethod: paymentMethod || undefined,
      paymentScreenshotUrl: screenshotUrl,
    });

    setIsSubmitting(false);

    if (!donation) {
      toast.error('Failed to submit. Please try again.');
      return;
    }

    // Send donor confirmation email
    if (donorEmail.trim()) {
      sendPantryEmail('hub_donation_donor_confirmation', donorEmail.trim(), {
        donorName: donorName.trim(),
        hubName: selectedHub.name,
        items: itemsString,
      });
    }

    // Send hub owner notification
    if (selectedHub.email) {
      sendPantryEmail('hub_donation_received', selectedHub.email, {
        donorName: donorName.trim(),
        donorContact: donorContact.trim(),
        hubName: selectedHub.name,
        items: itemsString,
        message: message.trim(),
      });
    }

    setStep('done');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      {/* QR Lightbox */}
      {qrLightboxOpen && selectedHub?.qrCodeUrl && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm" onClick={() => setQrLightboxOpen(false)}>
          <div className="relative max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQrLightboxOpen(false)} className="absolute -top-10 right-0 text-white/80 hover:text-white"><X size={24} /></button>
            <img src={selectedHub.qrCodeUrl} alt={`QR code ng ${selectedHub.name}`} className="w-full rounded-2xl border-4 border-white/20 bg-white object-contain" />
            <a href={selectedHub.qrCodeUrl} download target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-xl transition-colors" onClick={(e) => e.stopPropagation()}>
              <Download size={15} /> Download QR Code
            </a>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-[#f0faf4] to-[#e8f5ee] border-b border-[#d1ead9] flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#2d7a4f] rounded-xl"><Heart size={18} className="text-white" /></div>
            <div>
              <h3 className="font-display font-700 text-[#1e5c38] text-base">Mag-donate</h3>
              <p className="text-xs text-[#6b7280]">Tulungan ang isang komunidad</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#6b7280] hover:bg-white/80 transition-colors"><X size={18} /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step === 'done' ? (
            <div className="px-6 py-10 text-center">
              <div className="w-20 h-20 bg-[#dcfce7] rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={40} className="text-[#16a34a]" />
              </div>
              <h4 className="font-display text-xl font-700 text-[#1e5c38] mb-2">Salamat sa iyong donasyon!</h4>
              <p className="text-sm text-[#6b7280] mb-1">Natanggap na namin ang iyong submission para sa</p>
              <p className="text-sm font-semibold text-[#2d7a4f] mb-4">{selectedHub?.name}</p>
              <p className="text-xs text-[#9ca3af]">Makikipag-ugnayan sa iyo ang hub para sa susunod na hakbang.</p>
              {donorEmail && <p className="text-xs text-[#9ca3af] mt-1">Nagpadala kami ng confirmation sa {donorEmail}.</p>}
              <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-[#2d7a4f] hover:bg-[#1e5c38] text-white text-sm font-semibold rounded-xl transition-all active:scale-95">Salamat!</button>
            </div>
          ) : step === 'select' ? (
            <div className="px-6 py-5 space-y-4">
              {/* Info banner */}
              <div className="flex items-start gap-3 bg-[#f0faf4] border border-[#d1ead9] rounded-xl px-4 py-3">
                <Info size={16} className="text-[#2d7a4f] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#4b5563] leading-relaxed">
                  Ang mga community pantry ay nasa ilalim ng <strong>Donation Hubs</strong> na namamahala ng mga donasyon. Mayroon silang sistema para maayos na maipamahagi ang mga donasyon sa iba&apos;t ibang lokasyon.
                </p>
              </div>

              <p className="text-sm font-semibold text-[#374151]">Piliin ang Donation Hub:</p>

              {loadingHubs ? (
                <div className="flex items-center justify-center py-8"><Loader2 size={24} className="animate-spin text-[#2d7a4f]" /></div>
              ) : hubs.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 size={28} className="text-[#d1ead9] mx-auto mb-3" />
                  <p className="text-sm text-[#9ca3af]">Walang donation hub na available ngayon.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {hubs.map((hub) => (
                    <button
                      key={hub.id}
                      onClick={() => handleSelectHub(hub)}
                      className="w-full flex items-center gap-4 px-4 py-4 rounded-xl border border-[#e5e7eb] hover:border-[#d1ead9] hover:bg-[#f8fdf9] text-left transition-all"
                    >
                      {hub.photoUrl ? (
                        <img src={hub.photoUrl} alt={`Photo ng ${hub.name}`} className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-[#d1ead9]" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-[#e8f5ee] flex items-center justify-center flex-shrink-0">
                          <Building2 size={20} className="text-[#2d7a4f]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1e5c38]">{hub.name}</p>
                        {hub.location && <p className="text-xs text-[#9ca3af] flex items-center gap-1 mt-0.5"><MapPin size={10} />{hub.location}</p>}
                        {hub.description && <p className="text-xs text-[#6b7280] mt-1 line-clamp-2">{hub.description}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : step === 'hub-profile' ? (
            <div className="px-6 py-5 space-y-4">
              {/* Preselected pantry note */}
              {preselectedPantryName && preselectedHubName && (
                <div className="flex items-start gap-3 bg-[#fffbeb] border border-[#fde68a] rounded-xl px-4 py-3">
                  <Info size={15} className="text-[#92400e] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#92400e]">
                    <strong>{preselectedPantryName}</strong> ay nasa ilalim ng <strong>{preselectedHubName}</strong>. Ang iyong donasyon ay mapupunta sa hub na ito.
                  </p>
                </div>
              )}

              {/* Hub profile */}
              {selectedHub && (
                <div className="space-y-4">
                  {selectedHub.photoUrl && (
                    <div className="rounded-xl overflow-hidden h-40 border border-[#d1ead9]">
                      <img src={selectedHub.photoUrl} alt={`Photo ng ${selectedHub.name}`} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-display font-700 text-[#1e5c38] text-lg">{selectedHub.name}</h4>
                    {selectedHub.description && <p className="text-sm text-[#4b5563] mt-1 leading-relaxed">{selectedHub.description}</p>}
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-sm">
                    {selectedHub.location && (
                      <div className="flex items-center gap-2 text-[#374151]">
                        <MapPin size={14} className="text-[#2d7a4f] flex-shrink-0" />
                        <span>{selectedHub.location}</span>
                      </div>
                    )}
                    {selectedHub.representativeName && (
                      <div className="flex items-center gap-2 text-[#374151]">
                        <User size={14} className="text-[#2d7a4f] flex-shrink-0" />
                        <span>{selectedHub.representativeName}</span>
                      </div>
                    )}
                    {selectedHub.contact && (
                      <div className="flex items-center gap-2 text-[#374151]">
                        <Phone size={14} className="text-[#2d7a4f] flex-shrink-0" />
                        <span>{selectedHub.contact}</span>
                      </div>
                    )}
                    {selectedHub.email && (
                      <div className="flex items-center gap-2 text-[#374151]">
                        <Mail size={14} className="text-[#2d7a4f] flex-shrink-0" />
                        <span>{selectedHub.email}</span>
                      </div>
                    )}
                  </div>

                  {selectedHub.qrCodeUrl && (
                    <div>
                      <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2 flex items-center gap-1.5"><QrCode size={12} /> QR Code para sa Donasyon</p>
                      <button type="button" onClick={() => setQrLightboxOpen(true)} className="group relative inline-block">
                        <img src={selectedHub.qrCodeUrl} alt={`QR code ng ${selectedHub.name}`} className="w-40 h-40 object-contain border-2 border-[#d1ead9] rounded-xl bg-white group-hover:opacity-80 transition-opacity" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-black/50 rounded-lg px-2 py-1 flex items-center gap-1">
                            <ZoomIn size={14} className="text-white" />
                            <span className="text-white text-xs font-medium">Enlarge</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Community Pantries under this hub */}
                  <div>
                    <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Store size={12} /> Mga Community Pantry sa Hub na Ito
                    </p>
                    {loadingHubPantries ? (
                      <div className="flex items-center gap-2 py-2">
                        <Loader2 size={14} className="animate-spin text-[#2d7a4f]" />
                        <span className="text-xs text-[#9ca3af]">Loading pantries…</span>
                      </div>
                    ) : hubPantries.length === 0 ? (
                      <p className="text-xs text-[#9ca3af] italic">Walang pantry na nakatala sa hub na ito.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {hubPantries.map((p) => (
                          <div key={p.id} className="flex items-center gap-2 px-3 py-2 bg-[#f8fdf9] border border-[#e8f5ee] rounded-lg">
                            <Store size={13} className="text-[#2d7a4f] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[#1e5c38] truncate">{p.pangalan}</p>
                              {(p.barangay || p.lungsod) && (
                                <p className="text-[10px] text-[#9ca3af]">{[p.barangay, p.lungsod].filter(Boolean).join(', ')}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Donation form */
            <div className="px-6 py-5 space-y-4">
              {/* Hub recap */}
              <div className="flex items-center gap-2 bg-[#f0faf4] border border-[#d1ead9] rounded-xl px-4 py-3">
                <Building2 size={15} className="text-[#2d7a4f] flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#9ca3af]">Donation Hub</p>
                  <p className="text-sm font-semibold text-[#1e5c38]">{selectedHub?.name}</p>
                </div>
              </div>

              {/* Donor name */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1.5">Pangalan Mo <span className="text-[#dc2626]">*</span></label>
                <input type="text" value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Juan dela Cruz" className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] ${errors.name ? 'border-[#fca5a5]' : 'border-[#d1d5db]'}`} />
                {errors.name && <p className="mt-1 text-xs text-[#dc2626]">{errors.name}</p>}
              </div>

              {/* Donor email */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1.5">Email Address <span className="text-[#9ca3af] font-normal">(Opsyonal — para sa confirmation)</span></label>
                <input type="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} placeholder="juan@email.com" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              </div>

              {/* Donor contact */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1.5">Contact Number <span className="text-[#dc2626]">*</span></label>
                <input type="text" value={donorContact} onChange={(e) => setDonorContact(e.target.value)} placeholder="09XX-XXX-XXXX" className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] ${errors.contact ? 'border-[#fca5a5]' : 'border-[#d1d5db]'}`} />
                {errors.contact && <p className="mt-1 text-xs text-[#dc2626]">{errors.contact}</p>}
              </div>

              {/* Donation type selection */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-2">Ano ang iyong ido-donate? <span className="text-[#dc2626]">*</span></label>
                <div className="flex gap-2 flex-wrap">
                  {([
                    { value: 'in-kind', label: 'In-kind' },
                    { value: 'cash', label: 'Cash / Fund Transfer' },
                    { value: 'both', label: 'Pareho (Both)' },
                  ] as { value: DonationType; label: string }[]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDonationType(opt.value)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${donationType === opt.value ? 'bg-[#2d7a4f] text-white border-[#2d7a4f]' : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#d1ead9]'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {errors.donationType && <p className="mt-1 text-xs text-[#dc2626]">{errors.donationType}</p>}
              </div>

              {/* In-kind items */}
              {(donationType === 'in-kind' || donationType === 'both') && (
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-1.5">Mga In-kind na Donasyon <span className="text-[#dc2626]">*</span></label>
                  <textarea value={inKindItems} onChange={(e) => setInKindItems(e.target.value)} rows={3} placeholder="hal. 5kg bigas, 10 lata ng sardinas, 2 boteng mantika..." className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] resize-none ${errors.inKindItems ? 'border-[#fca5a5]' : 'border-[#d1d5db]'}`} />
                  {errors.inKindItems && <p className="mt-1 text-xs text-[#dc2626]">{errors.inKindItems}</p>}
                </div>
              )}

              {/* Cash / Fund Transfer */}
              {(donationType === 'cash' || donationType === 'both') && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-[#374151] mb-1.5">Halaga / Cash Amount <span className="text-[#dc2626]">*</span></label>
                    <input type="text" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} placeholder="hal. ₱500 o anumang halaga" className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] ${errors.cashAmount ? 'border-[#fca5a5]' : 'border-[#d1d5db]'}`} />
                    {errors.cashAmount && <p className="mt-1 text-xs text-[#dc2626]">{errors.cashAmount}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#374151] mb-2">Paraan ng Pagbabayad</label>
                    <div className="flex gap-2 flex-wrap">
                      {(['gcash', 'bank', 'other'] as const).map((method) => (
                        <button
                          key={method}
                          type="button"
                          onClick={() => setPaymentMethod(paymentMethod === method ? '' : method)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === method ? 'bg-[#2d7a4f] text-white border-[#2d7a4f]' : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#d1ead9]'}`}
                        >
                          {method === 'gcash' ? 'GCash' : method === 'bank' ? 'Bank Transfer' : 'Iba pa'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Screenshot upload */}
                  {(paymentMethod === 'gcash' || paymentMethod === 'bank') && (
                    <div>
                      <label className="block text-sm font-semibold text-[#374151] mb-1.5 flex items-center gap-1.5"><Upload size={14} className="text-[#2d7a4f]" /> Screenshot ng Confirmation <span className="text-[#9ca3af] font-normal">(Opsyonal)</span></label>
                      {screenshotPreview ? (
                        <div className="relative inline-block">
                          <img src={screenshotPreview} alt="Screenshot ng payment confirmation" className="w-full max-w-xs h-40 object-contain border border-[#d1ead9] rounded-xl bg-[#f8fdf9]" />
                          <button type="button" onClick={() => { setScreenshotFile(null); setScreenshotPreview(null); if (screenshotRef.current) screenshotRef.current.value = ''; }} className="absolute -top-2 -right-2 w-6 h-6 bg-[#dc2626] text-white rounded-full flex items-center justify-center"><X size={12} /></button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => screenshotRef.current?.click()} className="flex flex-col items-center justify-center gap-2 w-full max-w-xs h-28 border-2 border-dashed border-[#d1ead9] rounded-xl hover:border-[#2d7a4f] hover:bg-[#f0faf4] transition-all text-[#9ca3af] hover:text-[#2d7a4f]">
                          <Upload size={20} /><span className="text-xs font-medium">I-upload ang Screenshot</span><span className="text-xs">PNG, JPG — max 10MB</span>
                        </button>
                      )}
                      <input ref={screenshotRef} type="file" accept="image/*" onChange={handleScreenshotChange} className="hidden" />
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1.5">Mensahe <span className="text-[#9ca3af] font-normal">(Opsyonal)</span></label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Anumang mensahe para sa hub..." className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] resize-none" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'done' && (
          <div className="px-6 py-4 border-t border-[#e8f5ee] flex-shrink-0 flex items-center justify-between gap-3">
            {step === 'select' ? (
              <button onClick={onClose} className="px-4 py-2.5 text-sm text-[#6b7280] hover:text-[#374151] transition-colors">Cancel</button>
            ) : step === 'hub-profile' ? (
              <>
                {!preselectedPantryId ? (
                  <button onClick={() => { setSelectedHub(null); setPreselectedHubName(null); setPreselectedPantryName(null); setHubPantries([]); setStep('select'); }} className="px-4 py-2.5 text-sm text-[#6b7280] hover:text-[#374151] transition-colors">← Back</button>
                ) : (
                  <button onClick={onClose} className="px-4 py-2.5 text-sm text-[#6b7280] hover:text-[#374151] transition-colors">Cancel</button>
                )}
                <button onClick={() => setStep('form')} className="px-5 py-2.5 bg-[#2d7a4f] hover:bg-[#1e5c38] text-white text-sm font-semibold rounded-xl transition-all active:scale-95">
                  Mag-donate dito →
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setStep('hub-profile')} className="px-4 py-2.5 text-sm text-[#6b7280] hover:text-[#374151] transition-colors">← Back</button>
                <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 px-5 py-2.5 bg-[#2d7a4f] hover:bg-[#1e5c38] disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-all active:scale-95">
                  {isSubmitting ? <><Loader2 size={15} className="animate-spin" /> Nagsu-submit…</> : <><Heart size={15} /> I-submit ang Donasyon</>}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
